// ----------------------------------------------------------------------- imports externos
import { useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";

// ----------------------------------------------------------------------- imports internos
import { traerChoferes } from "../services/rrhh";
import { etiquetaCorta, claseDeEmpresa } from "../shared/empresa";
import { rutaDePersona } from "../shared/personas";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";

/* Los choferes: la lista, y el nombre lleva a la ficha.
 *
 * ── SALE DE LA NÓMINA Y NO DEL PADRÓN ────────────────────────────────────
 *
 * Es la diferencia que hace que esta pantalla sirva. `personas` tiene 2.476
 * filas y su `estado` heredado de Firestore da 356 activos; adentro de esos
 * 356 hay cinco DNI inventados (`1000000`, `11111111`, `12345678`…), diez
 * proveedores marcados como personas y unas sesenta bajas que nadie registró.
 * Una lista armada sobre eso obliga a saber de memoria a quién ignorar.
 *
 * La nómina de sueldos son 292 personas y 170 choferes, y es contra lo que se
 * paga. Lo dijo Igna: *«acá lo que manda es lo que pase Erick»*.
 *
 * ── QUIÉN ES CHOFER ──────────────────────────────────────────────────────
 *
 * Sale de `Calificación Profesional` del export —«Chofer Larga Distancia» o
 * «Corta»—, que es lo que la persona HACE. No de `Categoría`, que es el
 * encuadre del convenio, ni de `Convenio = Choferes`, que es el CCT de
 * camioneros e incluye a los administrativos: por ahí serían 254.
 *
 * La cuenta la hace la base en `choferes_activos`; acá sólo se dibuja.
 *
 * ── NO SE EDITA, Y POR ESO NO TIENE LÁPIZ ────────────────────────────────
 *
 * El resto de los padrones se corrige en la celda. Éste no: el origen es el
 * archivo de Erick, así que una corrección acá duraría hasta la próxima
 * importación. Lo que esté mal se arregla en la nómina y vuelve a entrar.
 */

const ThOrden = ({ columna, orden, alTocar, children }) => {
  const activo = orden.columna === columna;
  return (
    <th>
      <button
        type="button"
        className={"th-orden" + (activo ? " activo" : "")}
        onClick={() => alTocar(columna)}
      >
        {children}
        <span className="th-flecha">
          {!activo ? "" : orden.desc ? " ↓" : " ↑"}
        </span>
      </button>
    </th>
  );
};

const texto = (v) => String(v ?? "").trim();

const Choferes = () => {
  const { permisos = null } = useOutletContext() || {};
  const puede = (p) => permisos === null || permisos.includes(p);

  const [filas, setFilas] = useState(undefined); // undefined = cargando
  const [error, setError] = useState(false);
  const [buscar, setBuscar] = useState("");
  const [orden, setOrden] = useState({ columna: "apellido", desc: false });

  useEffect(() => {
    let vigente = true;
    traerChoferes()
      .then((d) => vigente && setFilas(d))
      .catch(() => vigente && setError(true));
    return () => {
      vigente = false;
    };
  }, []);

  const alTocar = (columna) =>
    setOrden((o) =>
      o.columna === columna
        ? { columna, desc: !o.desc }
        : { columna, desc: false },
    );

  const lista = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const base = (filas || []).filter((f) =>
      !q
        ? true
        : [f.apellido, f.nombres, f.dni, f.legajo, f.destino]
            .map((v) => texto(v).toLowerCase())
            .some((v) => v.includes(q)),
    );
    const signo = orden.desc ? -1 : 1;
    return [...base].sort(
      (a, b) =>
        signo *
        texto(a[orden.columna]).localeCompare(texto(b[orden.columna]), "es", {
          numeric: true,
        }),
    );
  }, [filas, buscar, orden]);

  /* El permiso se dice, con la salida al lado: «no tengo la llave» y «esto
   * está roto» se ven igual en una pantalla vacía. */
  if (!puede("ver.gente")) {
    return <PuertaCerrada />;
  }

  if (error) {
    return (
      <div className="panel-aviso"><h3>No se pudo consultar</h3></div>
    );
  }

  return (
    <>
      <div className="tk-filtros">
        <input
          type="search"
          placeholder="Buscar por nombre, DNI, legajo o base"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          aria-label="Buscar chofer"
        />
        {/* El total se dice siempre. Una lista filtrada que no aclara cuántos
            hay en total se lee como si ésos fueran todos los choferes. */}
        <span className="tk-chip">
          {filas === undefined
            ? "cargando…"
            : buscar.trim()
              ? `${lista.length} de ${filas.length}`
              : `${filas.length} choferes`}
        </span>
      </div>

      {/* Vacío NO es lo mismo que "todavía no cargó", y tampoco que "no hay
          choferes": si la nómina nunca se importó la tabla está en cero, y
          decir «no hay choferes» mandaría a buscar el error en el lado
          equivocado. */}
      {filas !== undefined && filas.length === 0 && (
        <div className="panel-aviso suave">
          <h3>Todavía no se importó ninguna nómina</h3>
        </div>
      )}

      <div className="tabla-wrap">
        <table className="tabla fija">
          <thead>
            <tr>
              <ThOrden columna="apellido" orden={orden} alTocar={alTocar}>
                Chofer
              </ThOrden>
              <ThOrden columna="dni" orden={orden} alTocar={alTocar}>
                DNI
              </ThOrden>
              <ThOrden columna="legajo" orden={orden} alTocar={alTocar}>
                Legajo
              </ThOrden>
              <ThOrden columna="empresa" orden={orden} alTocar={alTocar}>
                Empresa
              </ThOrden>
              <ThOrden columna="destino" orden={orden} alTocar={alTocar}>
                Base
              </ThOrden>
              <ThOrden columna="calificacion" orden={orden} alTocar={alTocar}>
                Tarea
              </ThOrden>
            </tr>
          </thead>
          <tbody>
            {lista.map((f) => (
              <tr key={f.dni}>
                <td>
                  {/* El nombre es el enlace, que es lo que se pidió: «al
                      clickear en el nombre te abra las tarjetas con sus
                      datos». La ruta la arma `rutaDePersona` y no esta
                      pantalla, para que el día que cambie cambie en un lugar. */}
                  <Link className="rto-link" to={rutaDePersona(f.dni)}>
                    {texto(f.apellido)}, {texto(f.nombres)}
                  </Link>
                </td>
                <td className="dato">{texto(f.dni)}</td>
                <td className="dato">{texto(f.legajo) || "—"}</td>
                <td>
                  {/* El CUIT pelado no lo reconoce nadie: se muestra la sigla,
                      con el color que ya usa el resto del hub. Transcan es TCC
                      y su CUIT arranca con 33. */}
                  <span className={"pill " + claseDeEmpresa(f.empresa)}>
                    {etiquetaCorta(f.empresa)}
                  </span>
                </td>
                <td>{texto(f.destino) || "—"}</td>
                <td className="sub">{texto(f.calificacion) || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Choferes;
